import { useState, useEffect } from 'react'
import { motion } from 'framer-motion'
import { Star, User, Trash2, Eye, EyeOff, Plus, Edit2, Save, X, CheckCircle, XCircle, Download, Printer, FileJson } from 'lucide-react'
import { db } from '../../firebase'
import { collection, onSnapshot, addDoc, updateDoc, deleteDoc, doc, serverTimestamp } from 'firebase/firestore'
import ConfirmModal from '../ConfirmModal'
import { exportAsJSON, exportAsCSV, exportAsText, printItem } from '../../utils/helpers.jsx'

const ReviewsSection = () => {
  const [reviews, setReviews] = useState([])
  const [filteredReviews, setFilteredReviews] = useState([])
  const [filterStatus, setFilterStatus] = useState('all') // all, pending, approved, denied
  const [confirmModal, setConfirmModal] = useState({ isOpen: false, reviewId: null })
  const [newReview, setNewReview] = useState({
    name: '',
    role: '',
    company: '',
    rating: 5,
    text: '',
    image: ''
  })
  const [editingId, setEditingId] = useState(null)
  const [editData, setEditData] = useState({})
  const [stats, setStats] = useState({
    total: 0,
    pending: 0,
    approved: 0,
    denied: 0
  })

  useEffect(() => {
    const reviewsRef = collection(db, 'reviews')
    
    const unsubscribe = onSnapshot(reviewsRef, (snapshot) => {
      const loaded = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        createdAt: doc.data().createdAt?.toDate() || new Date(),
        status: doc.data().status || (doc.data().approved ? 'approved' : 'pending')
      }))
      loaded.sort((a, b) => b.createdAt - a.createdAt)
      setReviews(loaded)
      
      // Calculate stats
      const statsData = {
        total: loaded.length,
        pending: loaded.filter(r => r.status === 'pending').length,
        approved: loaded.filter(r => r.status === 'approved').length,
        denied: loaded.filter(r => r.status === 'denied').length
      }
      setStats(statsData)
    })

    return () => unsubscribe()
  }, [])

  // Filter reviews based on status
  useEffect(() => {
    if (filterStatus === 'all') {
      setFilteredReviews(reviews)
    } else {
      setFilteredReviews(reviews.filter(r => r.status === filterStatus))
    }
  }, [reviews, filterStatus])

  const handleAddReview = async (e) => {
    e.preventDefault()
    if (!newReview.name || !newReview.text) return

    try {
      await addDoc(collection(db, 'reviews'), {
        ...newReview,
        visible: true,
        status: 'approved',
        approved: true,
        createdAt: serverTimestamp()
      })
      setNewReview({ name: '', role: '', company: '', rating: 5, text: '', image: '' })
      alert('✅ Review u shtua dhe u aprovua automatikisht!')
    } catch (error) {
      console.error('Error adding review:', error)
      alert('❌ Gabim gjatë shtimit!')
    }
  }

  const handleToggleVisibility = async (id, currentVisibility) => {
    try {
      await updateDoc(doc(db, 'reviews', id), {
        visible: !currentVisibility
      })
    } catch (error) {
      console.error('Error toggling visibility:', error)
    }
  }

  const handleDelete = async (id) => {
    try {
      await deleteDoc(doc(db, 'reviews', id))
    } catch (error) {
      console.error('Error deleting review:', error)
    }
  }

  const handleApprove = async (id) => {
    try {
      await updateDoc(doc(db, 'reviews', id), {
        status: 'approved',
        approved: true,
        visible: true,
        approvedAt: serverTimestamp()
      })
      alert('✅ Review u aprovua dhe është visible në website!')
    } catch (error) {
      console.error('Error approving review:', error)
      alert('❌ Gabim gjatë aprovimit!')
    }
  }

  const handleDeny = async (id) => {
    try {
      await updateDoc(doc(db, 'reviews', id), {
        status: 'denied',
        approved: false,
        visible: false,
        deniedAt: serverTimestamp()
      })
      alert('❌ Review u refuzua!')
    } catch (error) {
      console.error('Error denying review:', error)
      alert('❌ Gabim gjatë refuzimit!')
    }
  }

  const openDeleteConfirm = (id) => {
    setConfirmModal({ isOpen: true, reviewId: id })
  }

  const closeDeleteConfirm = () => {
    setConfirmModal({ isOpen: false, reviewId: null })
  }

  const confirmDelete = () => {
    if (confirmModal.reviewId) {
      handleDelete(confirmModal.reviewId)
    }
  }

  const handleEdit = (review) => {
    setEditingId(review.id)
    setEditData(review)
  }

  const handleSaveEdit = async () => {
    try {
      const { id, ...dataToUpdate } = editData
      await updateDoc(doc(db, 'reviews', id), dataToUpdate)
      setEditingId(null)
      setEditData({})
      alert('Review u përditësua!')
    } catch (error) {
      console.error('Error updating review:', error)
      alert('Gabim gjatë përditësimit!')
    }
  }

  const handleCancelEdit = () => {
    setEditingId(null)
    setEditData({})
  }

  const renderStars = (rating) => {
    return [...Array(5)].map((_, i) => (
      <Star
        key={i}
        className={`w-4 h-4 ${i < rating ? 'fill-gold-400 text-gold-400' : 'text-beige-400'}`}
      />
    ))
  }

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-3xl font-display font-bold text-gradient-gold mb-2">
          Menaxho Recensionet
        </h2>
        <p className="text-beige-300">Shto, aprovo, ndrysho ose fshi recensione të klientëve</p>
      </div>

      {/* Statistics Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-bronze-900/30 backdrop-blur-sm border border-gold-500/20 rounded-xl p-4 hover:border-gold-500/40 transition-all"
        >
          <div className="text-2xl font-bold text-cream">{stats.total}</div>
          <div className="text-sm text-beige-400">Total Reviews</div>
        </motion.div>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-yellow-900/20 backdrop-blur-sm border border-yellow-500/30 rounded-xl p-4 hover:border-yellow-500/50 transition-all cursor-pointer"
          onClick={() => setFilterStatus('pending')}
        >
          <div className="text-2xl font-bold text-yellow-400">{stats.pending}</div>
          <div className="text-sm text-yellow-300">Në Pritje</div>
        </motion.div>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="bg-green-900/20 backdrop-blur-sm border border-green-500/30 rounded-xl p-4 hover:border-green-500/50 transition-all cursor-pointer"
          onClick={() => setFilterStatus('approved')}
        >
          <div className="text-2xl font-bold text-green-400">{stats.approved}</div>
          <div className="text-sm text-green-300">Aprovuar</div>
        </motion.div>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="bg-red-900/20 backdrop-blur-sm border border-red-500/30 rounded-xl p-4 hover:border-red-500/50 transition-all cursor-pointer"
          onClick={() => setFilterStatus('denied')}
        >
          <div className="text-2xl font-bold text-red-400">{stats.denied}</div>
          <div className="text-sm text-red-300">Refuzuar</div>
        </motion.div>
      </div>

      {/* Filter Buttons */}
      <div className="flex flex-wrap gap-2">
        {['all', 'pending', 'approved', 'denied'].map((status) => (
          <button
            key={status}
            onClick={() => setFilterStatus(status)}
            className={`px-4 py-2 rounded-lg font-medium transition-all ${
              filterStatus === status
                ? 'bg-gradient-gold text-white shadow-gold'
                : 'bg-bronze-900/30 text-beige-300 hover:bg-bronze-900/50'
            }`}
          >
            {status === 'all' && '🔍 Të Gjitha'}
            {status === 'pending' && '⏳ Në Pritje'}
            {status === 'approved' && '✅ Aprovuar'}
            {status === 'denied' && '❌ Refuzuar'}
          </button>
        ))}
      </div>

      {/* Add New Review Form */}
      <div className="bg-bronze-900/30 backdrop-blur-sm border border-gold-500/20 rounded-2xl p-6">
        <h3 className="text-lg font-display font-bold text-gold-400 mb-4 flex items-center">
          <Plus className="w-5 h-5 mr-2" />
          Shto Review të Re
        </h3>
        <form onSubmit={handleAddReview} className="space-y-4">
          <div className="grid md:grid-cols-2 gap-4">
            <input
              type="text"
              placeholder="Emri i klientit *"
              value={newReview.name}
              onChange={(e) => setNewReview({ ...newReview, name: e.target.value })}
              required
              className="px-4 py-3 bg-dark/50 border border-gold-500/30 rounded-xl text-cream placeholder-beige-400 focus:border-gold-400 focus:ring-2 focus:ring-gold-500/20 transition-all outline-none"
            />
            <input
              type="text"
              placeholder="Pozicioni (p.sh. CEO)"
              value={newReview.role}
              onChange={(e) => setNewReview({ ...newReview, role: e.target.value })}
              className="px-4 py-3 bg-dark/50 border border-gold-500/30 rounded-xl text-cream placeholder-beige-400 focus:border-gold-400 focus:ring-2 focus:ring-gold-500/20 transition-all outline-none"
            />
            <input
              type="text"
              placeholder="Kompania"
              value={newReview.company}
              onChange={(e) => setNewReview({ ...newReview, company: e.target.value })}
              className="px-4 py-3 bg-dark/50 border border-gold-500/30 rounded-xl text-cream placeholder-beige-400 focus:border-gold-400 focus:ring-2 focus:ring-gold-500/20 transition-all outline-none"
            />
            <div>
              <label className="block text-beige-300 text-sm mb-2">Rating (Yjet)</label>
              <select
                value={newReview.rating}
                onChange={(e) => setNewReview({ ...newReview, rating: parseInt(e.target.value) })}
                className="w-full px-4 py-3 bg-dark/50 border border-gold-500/30 rounded-xl text-cream focus:border-gold-400 focus:ring-2 focus:ring-gold-500/20 transition-all outline-none"
              >
                {[5, 4, 3, 2, 1].map(num => (
                  <option key={num} value={num}>{num} ⭐</option>
                ))}
              </select>
            </div>
          </div>
          <input
            type="url"
            placeholder="URL e imazhit (opsionale)"
            value={newReview.image}
            onChange={(e) => setNewReview({ ...newReview, image: e.target.value })}
            className="w-full px-4 py-3 bg-dark/50 border border-gold-500/30 rounded-xl text-cream placeholder-beige-400 focus:border-gold-400 focus:ring-2 focus:ring-gold-500/20 transition-all outline-none"
          />
          <textarea
            placeholder="Teksti i review-it *"
            value={newReview.text}
            onChange={(e) => setNewReview({ ...newReview, text: e.target.value })}
            required
            rows={4}
            className="w-full px-4 py-3 bg-dark/50 border border-gold-500/30 rounded-xl text-cream placeholder-beige-400 focus:border-gold-400 focus:ring-2 focus:ring-gold-500/20 transition-all outline-none"
          />
          <button type="submit" className="btn-primary">
            <Plus className="w-4 h-4 mr-2" />
            Shto Review
          </button>
        </form>
      </div>

      {/* Reviews List */}
      <div>
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-xl font-display font-bold text-gold-400">
            {filterStatus === 'all' && `Të Gjitha Reviews (${filteredReviews.length})`}
            {filterStatus === 'pending' && `⏳ Në Pritje për Aprovim (${filteredReviews.length})`}
            {filterStatus === 'approved' && `✅ Aprovuar (${filteredReviews.length})`}
            {filterStatus === 'denied' && `❌ Refuzuar (${filteredReviews.length})`}
          </h3>
        </div>
        
        <div className="grid md:grid-cols-2 gap-6">
          {filteredReviews.map((review) => (
          <motion.div
            key={review.id}
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className={`bg-bronze-900/30 backdrop-blur-sm border rounded-2xl p-6 transition-all ${
              review.visible ? 'border-gold-500/20' : 'border-red-500/30 opacity-60'
            }`}
          >
            {editingId === review.id ? (
              // Edit Mode
              <div className="space-y-3">
                <input
                  type="text"
                  value={editData.name}
                  onChange={(e) => setEditData({ ...editData, name: e.target.value })}
                  className="w-full px-3 py-2 bg-dark/50 border border-gold-500/30 rounded-lg text-cream"
                  placeholder="Emri"
                />
                <input
                  type="text"
                  value={editData.role}
                  onChange={(e) => setEditData({ ...editData, role: e.target.value })}
                  className="w-full px-3 py-2 bg-dark/50 border border-gold-500/30 rounded-lg text-cream"
                  placeholder="Pozicioni"
                />
                <input
                  type="text"
                  value={editData.company}
                  onChange={(e) => setEditData({ ...editData, company: e.target.value })}
                  className="w-full px-3 py-2 bg-dark/50 border border-gold-500/30 rounded-lg text-cream"
                  placeholder="Kompania"
                />
                <select
                  value={editData.rating}
                  onChange={(e) => setEditData({ ...editData, rating: parseInt(e.target.value) })}
                  className="w-full px-3 py-2 bg-dark/50 border border-gold-500/30 rounded-lg text-cream"
                >
                  {[5, 4, 3, 2, 1].map(num => (
                    <option key={num} value={num}>{num} ⭐</option>
                  ))}
                </select>
                <textarea
                  value={editData.text}
                  onChange={(e) => setEditData({ ...editData, text: e.target.value })}
                  rows={4}
                  className="w-full px-3 py-2 bg-dark/50 border border-gold-500/30 rounded-lg text-cream"
                />
                <div className="flex space-x-2">
                  <button onClick={handleSaveEdit} className="btn-primary text-sm flex-1">
                    <Save className="w-4 h-4 mr-1" />
                    Ruaj
                  </button>
                  <button onClick={handleCancelEdit} className="btn-secondary text-sm flex-1">
                    <X className="w-4 h-4 mr-1" />
                    Anulo
                  </button>
                </div>
              </div>
            ) : (
              // View Mode
              <>
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center space-x-3">
                    {review.image ? (
                      <img src={review.image} alt={review.name} className="w-12 h-12 rounded-full object-cover" />
                    ) : (
                      <div className="w-12 h-12 bg-gradient-gold rounded-full flex items-center justify-center">
                        <User className="w-6 h-6 text-white" />
                      </div>
                    )}
                    <div>
                      <h4 className="font-semibold text-cream">{review.name}</h4>
                      {review.role && <p className="text-xs text-beige-400">{review.role}</p>}
                      {review.company && <p className="text-xs text-beige-500">{review.company}</p>}
                    </div>
                  </div>
                  <div className="flex">{renderStars(review.rating)}</div>
                </div>

                <p className="text-beige-200 text-sm leading-relaxed mb-4 whitespace-pre-wrap">
                  "{review.text || 'Nuk ka tekst të shkruar.'}"
                </p>

                <div className="flex items-center justify-between pt-4 border-t border-gold-500/10">
                  <div className="flex flex-col gap-1">
                    <span className="text-xs text-beige-400">
                      {review.createdAt.toLocaleDateString('sq-AL')}
                    </span>
                    {review.status === 'pending' && (
                      <span className="text-xs px-2 py-1 bg-yellow-500/20 text-yellow-400 rounded-full w-fit">
                        Në Pritje
                      </span>
                    )}
                    {review.status === 'denied' && (
                      <span className="text-xs px-2 py-1 bg-red-500/20 text-red-400 rounded-full w-fit">
                        Refuzuar
                      </span>
                    )}
                  </div>
                  <div className="flex space-x-2">
                    {review.status === 'pending' && (
                      <>
                        <button
                          onClick={() => handleApprove(review.id)}
                          className="p-2 hover:bg-green-500/20 text-green-400 rounded-lg transition-all"
                          title="Aprovo review"
                        >
                          <CheckCircle className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleDeny(review.id)}
                          className="p-2 hover:bg-red-500/20 text-red-400 rounded-lg transition-all"
                          title="Refuzo review"
                        >
                          <XCircle className="w-4 h-4" />
                        </button>
                      </>
                    )}
                    <button
                      onClick={() => handleToggleVisibility(review.id, review.visible)}
                      className={`p-2 rounded-lg transition-all ${
                        review.visible
                          ? 'hover:bg-green-500/20 text-green-400'
                          : 'hover:bg-red-500/20 text-red-400'
                      }`}
                      title={review.visible ? 'Fshih nga website' : 'Shfaq në website'}
                    >
                      {review.visible ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
                    </button>
                    <button
                      onClick={() => handleEdit(review)}
                      className="p-2 hover:bg-gold-500/20 rounded-lg transition-all"
                      title="Edit review"
                    >
                      <Edit2 className="w-4 h-4 text-beige-300" />
                    </button>
                    
                    {/* Export Dropdown */}
                    <div className="relative group">
                      <button
                        className="p-2 hover:bg-blue-500/20 rounded-lg transition-all"
                        title="Export review"
                      >
                        <Download className="w-4 h-4 text-blue-400" />
                      </button>
                      <div className="absolute right-0 mt-2 w-40 bg-bronze-900 border border-gold-500/30 rounded-lg shadow-gold opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all z-10">
                        <button
                          onClick={() => exportAsJSON(review, `review-${review.name.replace(/\s/g, '-').toLowerCase()}`)}
                          className="w-full px-4 py-2 text-left text-sm text-beige-200 hover:bg-gold-500/20 flex items-center rounded-t-lg"
                        >
                          <FileJson className="w-3 h-3 mr-2" />
                          Export JSON
                        </button>
                        <button
                          onClick={() => exportAsCSV(review, `review-${review.name.replace(/\s/g, '-').toLowerCase()}`)}
                          className="w-full px-4 py-2 text-left text-sm text-beige-200 hover:bg-gold-500/20 flex items-center"
                        >
                          <Download className="w-3 h-3 mr-2" />
                          Export CSV
                        </button>
                        <button
                          onClick={() => exportAsText(review, `review-${review.name.replace(/\s/g, '-').toLowerCase()}`, `Review - ${review.name}`)}
                          className="w-full px-4 py-2 text-left text-sm text-beige-200 hover:bg-gold-500/20 flex items-center"
                        >
                          <Download className="w-3 h-3 mr-2" />
                          Export TXT
                        </button>
                        <button
                          onClick={() => printItem(review, `Review - ${review.name}`)}
                          className="w-full px-4 py-2 text-left text-sm text-beige-200 hover:bg-gold-500/20 flex items-center rounded-b-lg"
                        >
                          <Printer className="w-3 h-3 mr-2" />
                          Print
                        </button>
                      </div>
                    </div>
                    
                    <button
                      onClick={() => openDeleteConfirm(review.id)}
                      className="p-2 hover:bg-red-500/20 rounded-lg transition-all"
                      title="Delete review"
                    >
                      <Trash2 className="w-4 h-4 text-red-400" />
                    </button>
                  </div>
                </div>
              </>
            )}
          </motion.div>
        ))}
      </div>
      </div>

      {filteredReviews.length === 0 && (
        <div className="text-center py-12 bg-bronze-900/20 rounded-2xl">
          <Star className="w-16 h-16 text-beige-400 mx-auto mb-4 opacity-50" />
          <p className="text-beige-300">
            {filterStatus === 'all' && 'Nuk ka reviews ende. Shto të parën!'}
            {filterStatus === 'pending' && 'Nuk ka reviews në pritje.'}
            {filterStatus === 'approved' && 'Nuk ka reviews të aprovuara ende.'}
            {filterStatus === 'denied' && 'Nuk ka reviews të refuzuara.'}
          </p>
        </div>
      )}

      {/* Confirm Delete Modal */}
      <ConfirmModal
        isOpen={confirmModal.isOpen}
        onClose={closeDeleteConfirm}
        onConfirm={confirmDelete}
        title="Fshi Review"
        message="Dëshiron ta fshish këtë review? Ky veprim nuk mund të zhbëhet."
        confirmText="Po, Fshije"
        cancelText="Jo, Anulo"
      />
    </div>
  )
}

export default ReviewsSection
